import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { useAuth } from '@backrs_ui/grow-ui/src/hooks/useAuth';
import { openSignupPrompt } from '@backrs_ui/grow-ui/src/redux/appState/appState';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { startFreshRun } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { closeCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box } from '@mui/material';

import { BoosterLoadError } from './components/BoosterLoadError';
import { BoosterLoading } from './components/BoosterLoading';
import { BoosterResultActions } from './components/BoosterResultActions';
import { TestBoosterBanner } from './components/TestBoosterBanner';
import { useBoosterPlan } from './hooks/useBoosterPlan';
import { useBoosterPlanSummary } from './hooks/useBoosterPlanSummary';
import { boosterPageSx } from './satActBooster.styles';
import { useAgentContext } from './SatActBoosterResultPageContainer.agentContext';
import { SatActResultsPage } from './SatActResultsPage';

/**
 * The Test Booster output page — its own route, so there is no back into the wizard. Reads the
 * caller's most recent *finished* submission, so a run abandoned after it can't hide this plan: it
 * renders (blurred behind the signup gate for guests), and only a caller with no finished plan at
 * all is redirected to the wizard to build one.
 */
export const SatActBoosterResultPageContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loggedIn } = useAuth();
  const { mostRecent, isPending, loadFailed, hasPlan, refetch } = useBoosterPlan();

  const reportFacts = mostRecent?.reportFacts ?? null;
  const plan = mostRecent?.plan ?? null;

  useEffect(() => {
    if (!loggedIn && hasPlan) {
      dispatch(openSignupPrompt('sat_act_booster'));
    }
  }, [loggedIn, hasPlan, dispatch]);

  // The Daily Practice tab is opened from this page's calendar, so it must not outlive the route.
  useEffect(() => () => void dispatch(closeCustomTab('dailyPractice')), [dispatch]);

  useAgentContext(reportFacts, plan);
  // Declared after useAgentContext so the plan facts register before the summary is queued. Skipped
  // for guests: their plan is behind the signup gate.
  useBoosterPlanSummary(plan !== null && loggedIn);

  // Wait for any in-flight fetch (e.g. the refetch right after finalize) before deciding.
  if (isPending) {
    return <BoosterLoading />;
  }

  // A real fetch failure must not masquerade as "no submission" and bounce a finished student.
  if (loadFailed) {
    return <BoosterLoadError onRetry={refetch} />;
  }

  // No finished plan to show → send them to the wizard to build one.
  if (!hasPlan) {
    return <Navigate to={APP_ROUTE.SAT_ACT_BOOSTER} replace />;
  }

  // A finished run whose plan block didn't resolve is a load failure, not a thin plan:
  // rendering the page around it would show the banner and nothing else.
  if (!plan) {
    return <BoosterLoadError onRetry={refetch} />;
  }

  return (
    <Box sx={boosterPageSx}>
      {/* A guest has no account to own a bookmark or a task, so the actions are theirs only. */}
      <TestBoosterBanner actions={loggedIn ? <BoosterResultActions /> : undefined} />
      <SatActResultsPage
        plan={plan}
        test={reportFacts?.test ?? null}
        blurred={!loggedIn}
        onSignup={() => dispatch(openSignupPrompt('sat_act_booster'))}
        onGoBack={() => {
          dispatch(startFreshRun());
          navigate(APP_ROUTE.SAT_ACT_BOOSTER);
        }}
      />
    </Box>
  );
};
